/**
 * Registry of per-connector setup components.
 * Each connector that needs a post-auth (or API-key) setup registers its component here.
 * The orchestrator (DataSourceFlow) renders the right setup based on connector_type.
 */
import React, { lazy, Suspense } from 'react';
import { Loader2 } from 'lucide-react';

export interface ConnectorSetupProps {
  /** Set after OAuth callback; undefined for API-key flow until connector creates credential */
  credentialId: string | null;
  parentFolderId: string | null;
  /** If true, create data source as Knowledge Base (folder_type=knowledge_base, not in chat tree) */
  asKnowledgeBase?: boolean;
  connectorType: string;
  onCreated: (dataSourceId: string) => void;
  onBack: () => void;
}

export type ConnectorSetupComponent = React.ComponentType<ConnectorSetupProps>;

const CONNECTOR_SETUP_REGISTRY: Record<string, React.LazyExoticComponent<ConnectorSetupComponent>> = {
  google_drive: lazy(() => import('./googleDrive/GoogleDriveSetup').then(m => ({ default: m.GoogleDriveSetup }))),
  google_search_console: lazy(() => import('./googleSearchConsole/GscSetup').then(m => ({ default: m.GscSetup }))),
};

const SuspenseFallback = () => (
  <div className="flex items-center justify-center py-12">
    <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
  </div>
);

export function getSetupComponent(connectorType: string): React.ComponentType<ConnectorSetupProps> | null {
  const Lazy = CONNECTOR_SETUP_REGISTRY[connectorType];
  if (!Lazy) return null;
  const Wrapped: React.FC<ConnectorSetupProps> = (props) => (
    <Suspense fallback={<SuspenseFallback />}>
      <Lazy {...props} />
    </Suspense>
  );
  return Wrapped;
}

export function hasSetup(connectorType: string): boolean {
  return connectorType in CONNECTOR_SETUP_REGISTRY;
}

export function getRegisteredConnectorIds(): string[] {
  return Object.keys(CONNECTOR_SETUP_REGISTRY);
}

/**
 * Renders the connector-specific setup component with the given props.
 * Use this from the orchestrator when credentialId + connectorType are present (OAuth callback)
 * or when step is connector_setup for API-key connectors.
 */
export function RenderConnectorSetup(props: ConnectorSetupProps): React.ReactElement | null {
  const Component = getSetupComponent(props.connectorType);
  if (!Component) return null;
  return <Component {...props} />;
}
